/**
 * Small skill-list helpers shared by the Hunt slot merge and profile
 * conversion. Kept out of `conversation.ts` because that file is a
 * `"use server"` module where every export must be an async Server Action.
 */

/**
 * Dedupe a string array case-insensitively, preserving the first-seen casing
 * and trimming whitespace. Empty/blank entries are dropped.
 *
 * Without this, the Hunt slot union-merge kept both "React" (from
 * preferredRoles/profile) and "react" (from résumé extraction), polluting
 * `topSkills` — bloating the candidate-pool LIKE query and the "what I know"
 * chips.
 */
export function dedupeFoldCase(items: readonly string[]): string[] {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const raw of items) {
		const trimmed = raw.trim();
		const key = trimmed.toLowerCase();
		if (!key || seen.has(key)) continue;
		seen.add(key);
		out.push(trimmed);
	}
	return out;
}

/**
 * Synonym/alias map for candidate-pool retrieval. The pool query matches a
 * user's skill against job skill tags with a substring `LIKE '%skill%'`, which
 * silently under-matches when the user and the job tag the same concept with
 * different words — worst in non-tech verticals:
 *
 *   - a "content marketing" seeker never matches the ~hundreds of roles tagged
 *     just "content" / "copywriting" (the literal phrase appears in ~7 jobs);
 *   - an "seo" seeker misses roles tagged "search engine optimization".
 *
 * Result observed in live testing: a marketing seeker got video-editor cards
 * (which tag "social media content") while the real marketing roles sat
 * unmatched. Each key expands to broader/variant tokens that ARE how jobs tag
 * the concept. Keys + values must be lowercase. Substring matches don't need
 * an entry (e.g. "react" already matches "react.js").
 */
const SKILL_SYNONYMS: Record<string, readonly string[]> = {
	// Marketing / content (the main gap)
	"content marketing": ["content", "content strategy", "copywriting"],
	"content strategy": ["content", "copywriting"],
	"content writing": ["content", "copywriting"],
	copywriting: ["content", "copywriter"],
	seo: ["search engine optimization"],
	sem: ["search engine marketing", "paid search", "google ads"],
	"social media": ["social media marketing", "community management"],
	"social media marketing": ["social media", "smm"],
	"email marketing": ["email", "lifecycle marketing", "crm"],
	ppc: ["paid ads", "paid search", "google ads", "performance marketing"],
	"digital marketing": [
		"marketing",
		"growth marketing",
		"performance marketing",
	],
	// Adjacent non-tech verticals
	"graphic design": ["design", "visual design"],
	"ui/ux": ["ux", "ui", "product design"],
	"data analysis": ["analytics", "data analytics"],
	"project management": ["scrum", "agile", "pmp"],
	"customer success": ["customer support", "account management"],
	// Jobs tag this work "customer service" 4.6x more often than "customer
	// support" (520 vs 113 active rows, D1 2026-10-24).
	"customer support": ["customer service", "customer-service"],
	"customer service": ["customer support", "customer-service"],
	"pelayanan pelanggan": ["customer service", "customer support"],
	"layanan pelanggan": ["customer service", "customer support"],
	bookkeeping: ["accounting"],
	// Tech aliases where the user's short form won't substring-match the tag
	"machine learning": ["ml", "deep learning"],
	"search engine optimization": ["seo"],
};

/** Cap on expanded LIKE tokens — keeps the pool query under D1's 100-variable
 *  limit (each token is 2 bound params: skillsRequired + skillsBonus). */
const SKILL_MATCH_CAP = 45;

/**
 * Expand a user's skills with retrieval synonyms (see {@link SKILL_SYNONYMS}),
 * deduped case-insensitively and capped for the D1 variable limit. Used ONLY
 * to widen candidate-pool RETRIEVAL — ranking still scores on the user's real
 * skills, so synonym-pulled jobs only rank well if they genuinely fit.
 */
export function expandSkillSynonyms(skills: readonly string[]): string[] {
	const out: string[] = [];
	for (const raw of skills) {
		const trimmed = raw.trim();
		if (!trimmed) continue;
		out.push(trimmed);
		const syns = SKILL_SYNONYMS[trimmed.toLowerCase()];
		if (syns) out.push(...syns);
	}
	return dedupeFoldCase(out).slice(0, SKILL_MATCH_CAP);
}
